import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import type { Icon } from "@phosphor-icons/react";

export interface NavItem {
  href: string;
  label: string;
  icon?: Icon;
}

export interface NavItemWithSubmenu extends NavItem {
  submenu?: NavItem[];
}

interface NavItemProps {
  item: NavItem | NavItemWithSubmenu;
  openSubmenu: string | null;
  toggleSubmenu: (label: string) => void;
  onNavClick: () => void;
}

export const isNavItemWithSubmenu = (
  item: NavItem | NavItemWithSubmenu,
): item is NavItemWithSubmenu => {
  return "submenu" in item && Array.isArray(item.submenu);
};

export function NavItem({
  item,
  openSubmenu,
  toggleSubmenu,
  onNavClick,
}: NavItemProps) {
  const isOpen = openSubmenu === item.label;
  const IconComponent = item.icon;

  if (isNavItemWithSubmenu(item)) {
    return (
      <div className="nav-menu-item">
        <motion.button
          onClick={() => toggleSubmenu(item.label)}
          className={`nav-item flex items-center gap-2 ${isOpen ? "active" : ""}`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {IconComponent && <IconComponent className="w-5 h-5" />}
          <span>{item.label}</span>
          <motion.svg
            className="h-4 w-4 ml-auto"
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 9l-7 7-7-7"
            />
          </motion.svg>
        </motion.button>
        <AnimatePresence>
          {isOpen && item.submenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-1 space-y-1 pl-6">
                {item.submenu.map((subItem) => (
                  <motion.div
                    key={subItem.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link to={subItem.href} onClick={onNavClick} className="nav-item flex items-center gap-2">
                      {subItem.icon && <subItem.icon className="w-4 h-4" />}
                      <span>{subItem.label}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link to={item.href} onClick={onNavClick} className="nav-item flex items-center gap-2">
      {IconComponent && <IconComponent className="w-5 h-5" />}
      <span>{item.label}</span>
    </Link>
  );
}
